<script setup lang="ts">
    import { useRoute, useRouter } from 'vue-router';
    import { __ } from '../stores/expressions';
    import LoadingDots from '../components/LoadingDots.vue';
    import PageHeader from '../components/PageHeader.vue';
    import {
        useMutationDeleteProtocols,
        useMutationPatchProtocol,
        useQueryGetProtocol,
    } from '../services/api/protocol/protocolQueries';
    import { useConfirmationDialog } from '../stores/confirmationDialog';
    import { computed, onMounted, ref, watch } from 'vue';
    import EchoButton from '../components/EchoButton.vue';
    import ProtocolDetailInfo from '../components/coaching/protocol/detail/ProtocolDetailInfo.vue';
    import { useGetInteractions } from '../hooks/interaction';
    import EchoPopover from '../components/EchoPopover.vue';
    import TranscriptionVisualization from '../components/interactions/transcription/TranscriptionVisualization.vue';
    import TranscriptionChat from '../components/interactions/transcription/TranscriptionChat.vue';
    import CoachingFormController from '../components/coaching/form/CoachingFormController.vue';
    import { useCoachInteractionStore } from '../stores/coach-interaction';
    import ProtocolTotal from '../components/coaching/protocol/detail/ProtocolTotal.vue';
    import { dateToString } from '../utils/dateUtils';
    import EchoLabel from '../components/EchoLabel.vue';
    import EchoInfoBox from '../components/EchoInfoBox.vue';
    import EchoTooltip from '../components/EchoTooltip.vue';
    import { generatePatchProtocolRequest } from '../components/coaching/form/coachingForm.utils';
    import { usePermissions } from '../composables/usePermissions';
    import { useProtocolLock } from '../composables/useProtocolLock';
    import {
        InteractionModulePermissionFlag,
        PermissionArea,
        QualityAssuranceModulePermissionFlag,
    } from '../consts/permissions';
    import {
        CheckIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        ClipboardCheckIcon,
        HistoryIcon,
        PencilIcon,
        TrashIcon,
        XIcon,
    } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canAccessQA = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.Access
    );

    const canStream = hasPermission(
        PermissionArea.InteractionModule,
        InteractionModulePermissionFlag.Streaming
    );

    const canEditAiCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.EditAiCoach
    );

    const canEditManualCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.EditManualCoach
    );

    const canEditProtocol = computed(() => {
        if (!protocol.value) return false;
        return protocol.value.createdByAi ? canEditAiCoach.value : canEditManualCoach.value;
    });

    const route = useRoute();
    const router = useRouter();

    const coachInteractionStore = useCoachInteractionStore();

    const { data: protocolData, isLoading: protocolIsLoading } = useQueryGetProtocol(
        route.params.protocolId as string
    );

    const protocolTitle = computed(() => {
        if (protocolData.value) {
            return __('protocol-detail-title', {
                protocolNumber: String(protocolData.value.protocol.protocolNumber),
            });
        }

        return '';
    });

    const interactionPartId = computed(() => {
        if (!protocolData.value || !protocolData.value?.protocol.interactionPartId) {
            return [];
        }

        return [protocolData.value?.protocol.interactionPartId];
    });

    const {
        data: interactions,
        isLoading: interactionsIsLoading,
        refetch: refetchInteractions,
    } = useGetInteractions(interactionPartId);

    const protocolId = computed(() => {
        return route.params.protocolId as string;
    });

    const { mutateAsync } = useMutationPatchProtocol(protocolId);

    const deleteProtocols = useMutationDeleteProtocols();

    const onDeleteProtocol = async () => {
        const confirm = useConfirmationDialog();

        const confirmed = await confirm.openDialog({
            title: __('delete-protocol'),
            message: __('delete-protocol-confirmation'),
            confirmText: __('delete-protocol'),
        });

        if (!confirmed) return;

        await deleteProtocols.mutateAsync({ protocolIds: [protocolId.value] });
        await router.push('/quality-assurance/protocols');
    };

    const interaction = computed(() => {
        return interactions.value?.interactions[0];
    });

    const showAudioAccordion = computed(
        () => !!interaction.value && (interaction.value.channelType !== 0 || canStream.value)
    );

    const coachingForm = computed(() => {
        return protocolData.value?.coachingForm;
    });

    const protocol = computed(() => {
        return protocolData.value?.protocol;
    });

    const { lockReason } = useProtocolLock(protocol);

    const isLoading = computed(() => {
        return protocolIsLoading.value || interactionsIsLoading.value;
    });

    watch(
        () => interactionPartId.value,
        () => refetchInteractions(),
        { deep: true }
    );

    const currentTime = ref(0);
    const showTranscription = ref<boolean>(false);

    const editProtocol = ref(false);

    const cancelEditProtocol = () => {
        if (coachingForm.value && protocol.value) {
            coachInteractionStore.setProtocol(coachingForm.value, protocol.value);
        }

        editProtocol.value = false;
    };

    const saveProtocol = async () => {
        if (!coachInteractionStore.isValid) {
            return;
        }

        const request = generatePatchProtocolRequest(coachInteractionStore.protocol);

        await mutateAsync(request).then(() => {
            editProtocol.value = false;
        });
    };

    onMounted(() => {
        window.scrollTo(0, 0);
    });
</script>
<template>
    <div class="page-padding flex flex-col gap-2">
        <PageHeader v-if="!isLoading && protocol">
            <template #icon><ClipboardCheckIcon class="size-full" /></template>
            {{ protocolTitle }}

            <template #links>
                <EchoTooltip
                    v-if="!editProtocol && canAccessQA && canEditProtocol"
                    :label="lockReason ?? ''"
                    :disabled="!lockReason"
                    :tabindex="lockReason ? 0 : undefined"
                    class="flex gap-2"
                >
                    <EchoButton
                        variant="primary"
                        :disabled="!!lockReason"
                        @click="editProtocol = true"
                    >
                        {{ __('edit') }}
                        <template #icon-right><PencilIcon /></template>
                    </EchoButton>
                    <EchoButton
                        variant="secondary"
                        :disabled="!!lockReason"
                        @click="onDeleteProtocol()"
                    >
                        {{ __('delete') }}
                        <template #icon-right><TrashIcon /></template>
                    </EchoButton>
                </EchoTooltip>

                <div v-if="editProtocol" class="flex gap-2">
                    <EchoButton
                        variant="primary"
                        :disabled="!coachInteractionStore.isValid"
                        @click="saveProtocol()"
                    >
                        {{ __('save') }}
                        <template #icon-right><CheckIcon /></template>
                    </EchoButton>
                    <EchoButton variant="secondary" @click="cancelEditProtocol()">
                        {{ __('cancel') }}
                        <template #icon-right><XIcon /></template>
                    </EchoButton>
                </div>
            </template>
        </PageHeader>
        <template v-if="!isLoading && protocol && coachingForm">
            <div class="Card flex flex-col gap-3">
                <EchoInfoBox v-if="protocol.isSyncedFromV1">
                    {{ __('protocol-synced-from-v1-banner') }}
                </EchoInfoBox>

                <div class="flex justify-between mb-2">
                    <EchoButton back-or-fallback="/quality-assurance/protocols" variant="tertiary">
                        <template #icon-left><ChevronLeftIcon /></template>
                        {{ __('protocol-back-button') }}
                    </EchoButton>

                    <ProtocolTotal
                        :points="coachInteractionStore.sectionPoints.total.points"
                        :max-points="coachInteractionStore.sectionPoints.total.maxPoints"
                    />
                </div>

                <div v-if="protocol.lastReadAtHistory?.length" class="flex mb-2 gap-3">
                    <EchoPopover>
                        <template #button="{ anchor, togglePopover, buttonAttrs }">
                            <EchoButton
                                :ref="anchor"
                                variant="primary"
                                size="small"
                                v-bind="buttonAttrs"
                                @click="togglePopover"
                            >
                                {{
                                    __('protocol-last-read', {
                                        date: dateToString(new Date(protocol.lastReadAtHistory[0])),
                                    })
                                }}
                                <template #icon-right><HistoryIcon /></template>
                            </EchoButton>
                        </template>
                        <template #popover>
                            <section>
                                <h2 class="text-body-bold flex items-center gap-1 mb-2">
                                    <HistoryIcon class="size-5" />
                                    {{ __('protocol-read-history') }}
                                </h2>
                                <ul class="text-body">
                                    <li v-for="date in protocol.lastReadAtHistory" :key="date">
                                        {{ dateToString(new Date(date)) }}
                                    </li>
                                </ul>
                            </section>
                        </template>
                    </EchoPopover>

                    <EchoLabel v-if="!protocol.readByAgent" variant="light">
                        {{ __('not-read-by-agent', { agent: protocol.agentName }) }}
                    </EchoLabel>

                    <EchoLabel v-if="protocol.createdByAi" variant="solid">
                        {{
                            protocol.editedBy ? __('coached-with-ai-edited') : __('coached-with-ai')
                        }}
                    </EchoLabel>
                </div>

                <div>
                    <ProtocolDetailInfo
                        :key="'protocol-detail-info' + protocol.updatedAt"
                        :interaction="interaction"
                        :protocol="protocol"
                        :editable="editProtocol"
                    />
                </div>

                <div v-if="interaction && showAudioAccordion">
                    <TranscriptionVisualization
                        :interaction="interaction"
                        @current-time-update="currentTime = $event"
                    />
                </div>

                <div class="border border-gray-light rounded-2xl">
                    <div class="px-12 py-6">
                        <div class="flex stretch h-full">
                            <div class="flex-3">
                                <div class="flex items-center justify-end pb-4 h-8">
                                    <EchoButton
                                        v-show="interaction && !showTranscription"
                                        variant="tertiary"
                                        @click="showTranscription = true"
                                    >
                                        <template #icon-left><ChevronLeftIcon /></template>
                                        {{ __('transcription') }}
                                    </EchoButton>
                                </div>
                                <div :class="showTranscription && 'pr-8'">
                                    <CoachingFormController
                                        :preview="!editProtocol"
                                        :form="coachingForm"
                                        :protocol="protocol"
                                    />
                                </div>
                            </div>
                            <div
                                v-if="interaction && showTranscription"
                                class="flex-2 border-gray-lightest border-l"
                            >
                                <div class="px-8 mb-5">
                                    <EchoButton
                                        variant="tertiary"
                                        @click="showTranscription = false"
                                    >
                                        {{ __('hide-transcription') }}
                                        <template #icon-right><ChevronRightIcon /></template>
                                    </EchoButton>
                                </div>
                                <div class="pl-8 block sticky top-0 left-0 -mr-8">
                                    <TranscriptionChat
                                        :interaction="interaction"
                                        :current-time="currentTime"
                                        :enable-keyword-filter="false"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="Card">
                <LoadingDots />
            </div>
        </template>
    </div>
</template>
